import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Button, ConfigProvider, Empty } from 'antd';
import { ArrowLeftOutlined, FullscreenOutlined } from '@ant-design/icons';
import LocationsMapboxGallery from '../locations_mapbox_gallery/LocationsMapboxGallery';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import BrandLogoDisplay from '../brands_overview_index_page/BrandLogoDisplay';

function hasMapCoordinate(value) {
	if (value === '' || value == null) {
		return false;
	}
	const numericValue = Number(value);
	return Number.isFinite(numericValue);
}

// Inline map: 570px tall, full width of the TableHoc content column. OverviewShell
// Content (0.5em) + TableHoc px-2 supply the ~16px horizontal gutter — no max-width
// cap so the map aligns with the toolbar and does not leave side gutters on wide screens.
const INLINE_MAP_CONTAINER_CLASSNAME =
	'box-border relative h-[570px] w-full overflow-hidden bg-white';

const INLINE_MAP_GALLERY_PROPS = {
	hideSearchWithinArea: true,
	baseLayerStyle: 'light',
	showNavControls: false,
	renderWorldCopies: true,
	fitBoundsPadding: 16,
	fitBoundsMaxZoom: 12,
	lockBoundsAfterFit: false,
	disableDragPan: false,
};

const FULLSCREEN_MAP_GALLERY_PROPS = {
	hideSearchWithinArea: true,
	baseLayerStyle: 'light',
	showNavControls: false,
	renderWorldCopies: true,
	fitBoundsPadding: 48,
	fitBoundsMaxZoom: 12,
	lockBoundsAfterFit: false,
	disableDragPan: false,
};

interface LocationsMapViewProps {
	locations: any[];
	popupRenderFunc: (record: any) => any;
	longitudeAccessor: (record: any) => any;
	latitudeAccessor: (record: any) => any;
	detailPathForRecord: (record: any) => string;
	// Inline mode: optional expand affordance that opens the fullscreen overlay.
	onExpand?: () => void;
	// Fullscreen overlay mode: when `fullscreen` is set the component renders the
	// full-screen overlay shell (header + toolbar slot + map/sidebar layout) and is
	// gated by `open`. Otherwise it renders the inline 570px panel.
	fullscreen?: boolean;
	open?: boolean;
	onClose?: () => void;
	toolbar?: React.ReactNode;
}

const LocationsMapView: FC<LocationsMapViewProps> = ({
	locations,
	popupRenderFunc,
	longitudeAccessor,
	latitudeAccessor,
	detailPathForRecord,
	onExpand,
	fullscreen = false,
	open = false,
	onClose,
	toolbar,
}) => {
	const overlayRef = useRef<HTMLDivElement>(null);

	const geolocatedEntities = useMemo(
		() =>
			locations.filter((record) => {
				const longitude = longitudeAccessor(record);
				const latitude = latitudeAccessor(record);
				return hasMapCoordinate(longitude) && hasMapCoordinate(latitude);
			}),
		[locations, longitudeAccessor, latitudeAccessor]
	);

	const handleLocationClick = useCallback(
		(record) => () => {
			const path = detailPathForRecord(record);
			window.open(path, '_blank', 'noopener,noreferrer');
		},
		[detailPathForRecord]
	);

	if (fullscreen) {
		if (!open) {
			return null;
		}

		return (
			<div
				ref={overlayRef}
				className="fixed inset-0 z-[1000] flex flex-col overflow-hidden bg-white"
			>
				{/*
				 * Render all antd popups (filter Popover, Select dropdowns, etc.) inside
				 * this fixed overlay rather than the default document.body portal. At body
				 * level they sit in a different stacking/event context than the overlay, so
				 * clicks on the filter dropdown options don't register; scoping the popup
				 * container to the overlay keeps them interactive.
				 */}
				<ConfigProvider getPopupContainer={() => overlayRef.current || document.body}>
					<div className="flex shrink-0 items-center gap-m px-l py-m">
						<Button type="default" icon={<ArrowLeftOutlined />} onClick={onClose}>
							Back
						</Button>
						<h3 className="h3regular mb-0 text-character-primary">
							Locations ({locations.length})
						</h3>
					</div>
					{toolbar}
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<div className="flex h-full min-h-0 w-full overflow-hidden bg-white">
							<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-l">
								{geolocatedEntities.length === 0 ? (
									<div className="flex h-full flex-1 items-center justify-center px-4">
										<Empty description="No locations with map coordinates to show." />
									</div>
								) : (
									<div className="relative h-full min-h-0 w-full overflow-hidden">
										<LocationsMapboxGallery
											key="fullscreen-map"
											popupRenderFunc={popupRenderFunc}
											geolocatedEntities={geolocatedEntities}
											longitudeAccessor={longitudeAccessor}
											latitudeAccessor={latitudeAccessor}
											{...FULLSCREEN_MAP_GALLERY_PROPS}
										/>
									</div>
								)}
							</div>
							<div className="flex w-[390px] shrink-0 flex-col overflow-y-auto overflow-x-hidden border-l border-t-0 border-solid border-grey-5">
								{locations.map((record) => (
									<button
										key={record.id}
										type="button"
										className="flex w-full cursor-pointer items-center gap-xs border-0 border-b border-solid border-grey-4 bg-white px-m py-l text-left hover:bg-grey-2"
										onClick={handleLocationClick(record)}
									>
										<div className="flex min-w-0 flex-1 flex-col gap-2xs">
											<span className="bodymedium truncate text-grey-9">{record.name}</span>
											<div className="flex min-w-0 items-end gap-xs text-character-secondary">
												<span className="bodyregular truncate">
													{nullSafeGet('buyerFacility.primaryAddress.city', record)}
												</span>
												<span className="footnoteregular shrink-0 uppercase">
													{nullSafeGet('locationType.name', record)}{' '}
													{nullSafeGet('storeId', record)}
												</span>
											</div>
										</div>
										<BrandLogoDisplay brand={nullSafeGet('brand', record)} />
									</button>
								))}
							</div>
						</div>
					</div>
				</ConfigProvider>
			</div>
		);
	}

	return (
		<div className={INLINE_MAP_CONTAINER_CLASSNAME}>
			{geolocatedEntities.length === 0 ? (
				<div className="flex h-full items-center justify-center">
					<Empty description="No locations with map coordinates to show." />
				</div>
			) : (
				<>
					{onExpand ? (
						<div className="absolute right-2 top-2 z-20">
							<Button
								type="default"
								size="small"
								shape="circle"
								className="flex h-8 w-8 cursor-pointer items-center justify-center border border-solid border-grey-5 bg-white p-0 !text-black/85 shadow-sm hover:!border-grey-6 hover:!text-black/85 focus:!border-grey-6 focus:!text-black/85 active:!border-grey-6 active:!text-black/85 hover:[&_.anticon]:!text-black/85 focus:[&_.anticon]:!text-black/85 active:[&_.anticon]:!text-black/85"
								icon={<FullscreenOutlined style={{ fontSize: 14, color: 'rgba(0,0,0,0.85)' }} />}
								title="Expand map"
								aria-label="Expand map to full screen"
								onClick={onExpand}
							/>
						</div>
					) : null}
					<LocationsMapboxGallery
						key="inline-map"
						popupRenderFunc={popupRenderFunc}
						geolocatedEntities={geolocatedEntities}
						longitudeAccessor={longitudeAccessor}
						latitudeAccessor={latitudeAccessor}
						{...INLINE_MAP_GALLERY_PROPS}
					/>
				</>
			)}
		</div>
	);
};

export default LocationsMapView;
